<template>
    <div class="ds-wrap h-full w-full flex flex-col text-left">
        <div class="ds-toolbar">
            <div class="ds-toolbar__row">
                <div class="ds-filters">
                    <div>
                        <label class="ds-filter__label">Semester</label>
                        <select v-model="semesterFilter" class="ds-select" @change="onSemesterFilterChange">
                            <option
                                v-for="opt in semesterFilterOptions"
                                :key="'sem-filter-' + opt.value + opt.label"
                                :value="opt.value"
                            >
                                {{ opt.label }}
                            </option>
                        </select>
                    </div>
                    <div v-if="showVillaFilter">
                        <label class="ds-filter__label">Villa</label>
                        <select v-model="villaFilter" class="ds-select" @change="onFilterChange">
                            <option value="">All</option>
                            <option
                                v-for="n in filterVillaOptions"
                                :key="'villa-' + n"
                                :value="String(n)"
                            >
                                {{ n }}
                            </option>
                        </select>
                    </div>
                    <div v-if="assignmentsView === 'list'" class="ds-search">
                        <search ref="searchRef" @on-search="onSearch" class-list="px-3 h-9"></search>
                    </div>
                </div>
                <div class="ds-toolbar__actions">
                    <button type="button" class="ds-export-btn" :disabled="isExportExcel" @click="generateReport">
                        <font-awesome-icon :icon="['fas', 'file-excel']" class="ds-export-btn__icon" />
                        <span>{{ isExportExcel ? "Exporting..." : "Export Excel" }}</span>
                    </button>
                    <button
                        v-if="canDelete && assignmentsView === 'list'"
                        type="button"
                        class="ds-btn ds-btn--danger"
                        :disabled="deletingSelected || selectedIds.length === 0"
                        @click="confirmBulkDelete"
                    >
                        <font-awesome-icon icon="trash" class="mr-2" />
                        <span>{{ deletingSelected ? "Deleting..." : "Delete Selected" }}</span>
                    </button>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--ghost" :disabled="bulkUploading" @click="downloadTemplate">
                        <font-awesome-icon :icon="['fas', 'download']" class="mr-2" />
                        <span>Download Template</span>
                    </button>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" :disabled="bulkUploading" @click="openBulkUpload">
                        <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" class="mr-2" />
                        <span>{{ bulkUploading ? "Uploading..." : "Bulk Upload" }}</span>
                    </button>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        Add assignment
                    </button>
                </div>
            </div>
            <input
                ref="bulkFileInput"
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                class="hidden"
                @change="onBulkFileSelected"
            />
        </div>

        <div class="flex-auto flex flex-col px-4 sm:px-8 pb-8">
            <div class="asm-tabs" role="tablist" aria-label="Assignments view">
                <button
                    type="button"
                    role="tab"
                    class="asm-tabs__tab"
                    :class="{ 'asm-tabs__tab--active': assignmentsView === 'list' }"
                    :aria-selected="assignmentsView === 'list' ? 'true' : 'false'"
                    @click="setAssignmentsView('list')"
                >
                    <font-awesome-icon :icon="['fas', 'list']" class="asm-tabs__icon" />
                    <span>List</span>
                </button>
                <button
                    type="button"
                    role="tab"
                    class="asm-tabs__tab"
                    :class="{ 'asm-tabs__tab--active': assignmentsView === 'map' }"
                    :aria-selected="assignmentsView === 'map' ? 'true' : 'false'"
                    @click="setAssignmentsView('map')"
                >
                    <font-awesome-icon :icon="['fas', 'th-large']" class="asm-tabs__icon" />
                    <span>Floor plan</span>
                </button>
            </div>

            <template v-if="assignmentsView === 'map'">
                <div v-if="!(semesterFilter || '').trim()" class="flex-auto flex justify-center items-center py-16">
                    <div class="text-center max-w-md px-4">
                        <div class="asm-map-empty-icon">
                            <font-awesome-icon :icon="['fas', 'calendar-alt']" />
                        </div>
                        <h2 class="text-xl font-semibold text-gray-800 mb-2">Select a semester</h2>
                        <p class="text-gray-500">Floor plan shows villa, room, and bed occupancy for one semester at a time.</p>
                    </div>
                </div>

                <div v-else-if="mapLoading" class="flex justify-center py-16">
                    <base-page-spinner type="spin" class-list="h-12"></base-page-spinner>
                </div>

                <div v-else-if="!mapVillas.length" class="flex-auto flex justify-center items-center py-16">
                    <div class="text-center max-w-md px-4">
                        <div class="asm-map-empty-icon">
                            <font-awesome-icon :icon="['fas', 'home']" />
                        </div>
                        <h2 class="text-xl font-semibold text-gray-800 mb-2">No layout configured</h2>
                        <p class="text-gray-500 mb-4">Configure villas, rooms, and beds for this semester before viewing the floor plan.</p>
                        <button type="button" class="ds-btn ds-btn--primary" @click="$emit('open-configuration', semesterFilter)">
                            Open Configuration
                        </button>
                    </div>
                </div>

                <div v-else class="asm-map">
                    <div class="asm-map__legend-bar">
                        <div class="asm-map__stats">
                            <div class="asm-map__stat">
                                <span class="asm-map__stat-value">{{ mapStats.occupied }}</span>
                                <span class="asm-map__stat-label">Occupied</span>
                            </div>
                            <div class="asm-map__stat">
                                <span class="asm-map__stat-value">{{ mapStats.available }}</span>
                                <span class="asm-map__stat-label">Available</span>
                            </div>
                            <div class="asm-map__stat">
                                <span class="asm-map__stat-value">{{ mapStats.total }}</span>
                                <span class="asm-map__stat-label">Total beds</span>
                            </div>
                            <div class="asm-map__stat">
                                <span class="asm-map__stat-value">{{ mapStats.villas }}</span>
                                <span class="asm-map__stat-label">Villas</span>
                            </div>
                        </div>
                        <div class="asm-map__legend">
                            <span class="asm-map__legend-item">
                                <span class="asm-bed-swatch asm-bed-swatch--occupied"></span>
                                Occupied
                            </span>
                            <span class="asm-map__legend-item">
                                <span class="asm-bed-swatch asm-bed-swatch--free"></span>
                                Available
                            </span>
                        </div>
                    </div>

                    <div class="asm-map__grid">
                        <article
                            v-for="villa in mapVillas"
                            :key="'map-villa-' + villa.villa_no"
                            class="asm-villa"
                        >
                            <header class="asm-villa__header">
                                <div>
                                    <h3 class="asm-villa__title">Villa {{ villa.villa_no }}</h3>
                                    <p class="asm-villa__sub">
                                        {{ villa.rooms.length }} {{ villa.rooms.length === 1 ? "room" : "rooms" }}
                                        · {{ villaBedTotal(villa) }} beds
                                    </p>
                                </div>
                                <div class="asm-villa__badge" :class="villaOccupancyClass(villa)">
                                    {{ villaOccupiedCount(villa) }} / {{ villaBedTotal(villa) }}
                                </div>
                            </header>

                            <div class="asm-villa__rooms">
                                <div
                                    v-for="room in villa.rooms"
                                    :key="'map-v' + villa.villa_no + '-r' + room.room_no"
                                    class="asm-room"
                                >
                                    <div class="asm-room__label">
                                        <span class="asm-room__name">Room {{ room.room_no }}</span>
                                        <span class="asm-room__meta">
                                            {{ roomOccupiedCount(room) }}/{{ (room.beds || []).length }}
                                        </span>
                                    </div>
                                    <div class="asm-room__beds">
                                        <button
                                            v-for="bed in room.beds"
                                            :key="'map-bed-' + bed.id"
                                            type="button"
                                            class="asm-bed"
                                            :class="bed.assignment ? 'asm-bed--occupied' : 'asm-bed--free'"
                                            :title="bedTooltip(bed)"
                                            @click="onMapBedClick(villa, room, bed)"
                                        >
                                            <span class="asm-bed__slot">B{{ bed.bed_slot }}</span>
                                            <span v-if="bed.assignment" class="asm-bed__student">
                                                {{ shortStudentLabel(bed.assignment) }}
                                            </span>
                                            <span v-else class="asm-bed__student asm-bed__student--muted">Free</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </article>
                    </div>
                </div>
            </template>

            <template v-else>
            <div v-if="loading" class="flex-auto flex flex-col">
                <table-shimmer :titleHeaders="tableHeaders" :rows="perPage" />
            </div>

            <div v-else-if="items.length === 0" class="flex-auto flex justify-center items-center py-16">
                <div class="text-center max-w-md px-4">
                    <div class="ds-empty-icon">🛏️</div>
                    <h2 class="text-xl font-semibold text-gray-800 mb-2">No accommodation records</h2>
                    <p class="text-gray-500 mb-4">Configure accommodation for a semester, then upload the Excel template or add assignments manually.</p>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" @click="openBulkUpload">
                        Bulk Upload
                    </button>
                </div>
            </div>

            <template v-else>
                <div class="ds-table-shell overflow-x-auto">
                    <table class="min-w-full text-left text-sm">
                        <thead>
                            <tr>
                                <th class="ds-th w-12">
                                    <input
                                        type="checkbox"
                                        class="ds-checkbox"
                                        :disabled="deletableItems.length === 0"
                                        :checked="allVisibleSelected"
                                        @change="toggleSelectAll"
                                    />
                                </th>
                                <th class="ds-th w-12">#</th>
                                <th class="ds-th">Semester</th>
                                <th class="ds-th">Villa</th>
                                <th class="ds-th">Room</th>
                                <th class="ds-th">Bed</th>
                                <th class="ds-th">Student ID</th>
                                <th class="ds-th">Name</th>
                                <th class="ds-th">Emirates ID</th>
                                <th class="ds-th">Program</th>
                                <th class="ds-th">Sponsor</th>
                                <th class="ds-th">Date Of Entry</th>
                                <th v-if="canManage" class="ds-th text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in items" :key="item.id" class="ds-row">
                                <td class="ds-td w-12">
                                    <input
                                        type="checkbox"
                                        class="ds-checkbox"
                                        :disabled="!canDeleteItem(item)"
                                        :value="String(item.id)"
                                        v-model="selectedIds"
                                        @click.stop
                                    />
                                </td>
                                <td class="ds-td text-gray-400">{{ rowNumber(index) }}</td>
                                <td class="ds-td">{{ item.semester || "—" }}</td>
                                <td class="ds-td font-semibold">{{ item.villa }}</td>
                                <td class="ds-td">{{ item.room_no }}</td>
                                <td class="ds-td">{{ item.bed_slot }}</td>
                                <td class="ds-td font-mono text-xs">{{ item.student_id || "—" }}</td>
                                <td class="ds-td">{{ studentName(item) }}</td>
                                <td class="ds-td text-xs">{{ studentEid(item) }}</td>
                                <td class="ds-td text-xs">{{ studentProgram(item) }}</td>
                                <td class="ds-td">{{ item.sponsor || "—" }}</td>
                                <td class="ds-td">{{ formatDate(item.date_of_entry) }}</td>
                                <td v-if="canManage" class="ds-td text-right whitespace-nowrap">
                                    <button v-if="canUpdate" type="button" title="Edit" class="ds-icon-btn" @click="openEdit(item)">
                                        <font-awesome-icon icon="pen" class="w-3 h-3 text-gray-700" />
                                    </button>
                                    <button v-if="canDeleteItem(item)" type="button" title="Delete" class="ds-icon-btn ds-icon-btn--danger" @click="confirmDelete(item)">
                                        <font-awesome-icon icon="trash" class="w-3 h-3 text-gray-700" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="mt-4" v-if="paginatorInfoWithItems && paginatorInfoWithItems.total">
                    <pagination :paginatorInfo="paginatorInfoWithItems" :setPage="setPage"></pagination>
                </div>
            </template>
            </template>
        </div>

        <transition name="fade">
            <div
                v-if="showFormModal"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
                @click.self="closeForm"
            >
                <div class="ds-form bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
                    <div class="ds-form__header">
                        <div class="ds-form__header-row">
                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">
                                    {{ editingId ? "Edit assignment" : "Add assignment" }}
                                </h3>
                                <p class="text-sm text-gray-500 mt-1">
                                    {{ entity }} · {{ campus }}<template v-if="form.semester"> · {{ form.semester }}</template>
                                </p>
                            </div>
                            <button
                                v-if="canViewFormLayout"
                                type="button"
                                class="ds-layout-btn"
                                @click="openLayoutPreview"
                            >
                                <font-awesome-icon :icon="['fas', 'sitemap']" class="mr-2" />
                                View configuration
                            </button>
                        </div>
                        <div v-if="canViewFormLayout" class="ds-layout-strip">
                            <div class="ds-layout-strip__item">
                                <span class="ds-layout-strip__label">Villas</span>
                                <span class="ds-layout-strip__value">{{ formLayoutSummary.villaCount }}</span>
                            </div>
                            <div class="ds-layout-strip__item">
                                <span class="ds-layout-strip__label">Capacity</span>
                                <span class="ds-layout-strip__value">{{ formLayoutSummary.capacity }}</span>
                            </div>
                            <div class="ds-layout-strip__item">
                                <span class="ds-layout-strip__label">Layout</span>
                                <span class="ds-layout-strip__value">{{ formLayoutSummary.layout }}</span>
                            </div>
                            <div class="ds-layout-strip__item">
                                <span class="ds-layout-strip__label">Identical</span>
                                <span class="ds-layout-strip__value">{{ formLayoutSummary.identical }}</span>
                            </div>
                        </div>
                        <p v-if="canViewFormLayout && occupancyLine" class="ds-occupancy-line">
                            {{ occupancyLine }}
                        </p>
                    </div>
                    <div class="ds-form__body grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div v-if="showFormSemesterField">
                            <label class="ds-label">Semester <span class="text-red-500">*</span></label>
                            <select v-model="form.semester" class="ds-input" @change="onFormSemesterChange">
                                <option value="">Select semester</option>
                                <option
                                    v-for="sem in formSemesterOptions"
                                    :key="'form-sem-' + sem"
                                    :value="sem"
                                >
                                    {{ sem }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Villa <span class="text-red-500">*</span></label>
                            <select v-model="form.villa" class="ds-input" :disabled="!formLayoutReady" @change="onFormVillaChange">
                                <option value="">Select villa</option>
                                <option
                                    v-for="villaNo in formVillaOptions"
                                    :key="'form-villa-' + villaNo"
                                    :value="String(villaNo)"
                                >
                                    {{ villaNo }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Room no. <span class="text-red-500">*</span></label>
                            <select v-model="form.room_no" class="ds-input" :disabled="!form.villa" @change="onFormRoomChange">
                                <option value="">Select room</option>
                                <option
                                    v-for="roomNo in formRoomOptions"
                                    :key="'form-room-' + roomNo"
                                    :value="String(roomNo)"
                                >
                                    {{ roomNo }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Bed slot <span class="text-red-500">*</span></label>
                            <select v-model="form.bed_slot" class="ds-input" :disabled="!form.room_no">
                                <option value="">Select bed</option>
                                <option
                                    v-for="bedSlot in formBedOptions"
                                    :key="'form-bed-' + bedSlot"
                                    :value="String(bedSlot)"
                                >
                                    {{ bedSlot }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Student ID <span class="text-red-500">*</span></label>
                            <input v-model="form.student_id" type="text" class="ds-input" placeholder="Student number" />
                        </div>
                        <div>
                            <label class="ds-label">Sponsor</label>
                            <template v-if="hasSponsorOptions">
                                <select v-model="form.sponsorSelect" class="ds-input" @change="onSponsorSelectChange">
                                    <option value="">Select sponsor</option>
                                    <option
                                        v-for="sp in sponsors"
                                        :key="'sponsor-' + sp.id"
                                        :value="sp.name"
                                    >
                                        {{ sp.name }}
                                    </option>
                                    <option :value="SPONSOR_OTHER">Other</option>
                                </select>
                                <input
                                    v-if="form.sponsorSelect === SPONSOR_OTHER"
                                    v-model="form.sponsorOther"
                                    type="text"
                                    class="ds-input mt-2"
                                    placeholder="Enter sponsor name"
                                />
                            </template>
                            <input
                                v-else
                                v-model="form.sponsor"
                                type="text"
                                class="ds-input"
                                placeholder="Enter sponsor name"
                            />
                        </div>
                        <div>
                            <label class="ds-label">Date Of Entry</label>
                            <input v-model="form.date_of_entry" type="date" class="ds-input" />
                        </div>
                    </div>
                    <div class="ds-form__footer">
                        <button type="button" class="ds-btn ds-btn--ghost" @click="closeForm">Cancel</button>
                        <button type="button" class="ds-btn ds-btn--primary" :disabled="saving" @click="saveForm">
                            {{ saving ? "Saving..." : "Save" }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="fade">
            <div
                v-if="showLayoutPreview"
                class="ds-layout-preview"
                @click.self="closeLayoutPreview"
            >
                <div class="ds-layout-preview__panel">
                    <div class="ds-layout-preview__header">
                        <div>
                            <h3 class="ds-layout-preview__title">Semester configuration</h3>
                            <p class="ds-layout-preview__sub">
                                {{ entity }} · {{ campus }} · {{ layoutPreviewSemester }}
                            </p>
                        </div>
                        <button type="button" class="ds-layout-preview__close" title="Close" @click="closeLayoutPreview">
                            <font-awesome-icon :icon="['fas', 'times']" />
                        </button>
                    </div>

                    <div class="ds-layout-preview__stats">
                        <div class="ds-layout-preview__stat">
                            <p class="ds-layout-preview__stat-value">{{ formLayoutSummary.villaCount }}</p>
                            <p class="ds-layout-preview__stat-label">Villas</p>
                        </div>
                        <div class="ds-layout-preview__stat">
                            <p class="ds-layout-preview__stat-value">{{ formLayoutSummary.capacity }}</p>
                            <p class="ds-layout-preview__stat-label">Total beds</p>
                        </div>
                        <div class="ds-layout-preview__stat">
                            <p class="ds-layout-preview__stat-value">{{ formLayoutSummary.layout }}</p>
                            <p class="ds-layout-preview__stat-label">Room / bed layout</p>
                        </div>
                        <div class="ds-layout-preview__stat">
                            <p class="ds-layout-preview__stat-value">{{ formLayoutSummary.identical }}</p>
                            <p class="ds-layout-preview__stat-label">Villas identical</p>
                        </div>
                    </div>

                    <div class="ds-layout-preview__body">
                        <div
                            v-for="villa in layoutVillas"
                            :key="'preview-villa-' + villa.villa_no"
                            class="ds-layout-villa"
                        >
                            <div class="ds-layout-villa__head">
                                <span class="ds-layout-villa__title">Villa {{ villa.villa_no }}</span>
                                <span class="ds-layout-villa__meta">{{ villaBedCount(villa) }} beds</span>
                            </div>
                            <div class="ds-layout-rooms">
                                <div
                                    v-for="room in villa.rooms || []"
                                    :key="'preview-room-' + villa.villa_no + '-' + room.room_no"
                                    class="ds-layout-room"
                                >
                                    <div class="ds-layout-room__title">Room {{ room.room_no }}</div>
                                    <div class="ds-layout-beds">
                                        <span
                                            v-for="slot in roomBedSlots(room)"
                                            :key="'preview-bed-' + villa.villa_no + '-' + room.room_no + '-' + slot"
                                            class="ds-layout-bed"
                                        >
                                            Bed {{ slot }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p v-if="!layoutVillas.length" class="text-sm text-gray-500">No layout details available.</p>
                    </div>

                    <div class="ds-layout-preview__footer">
                        <button type="button" class="ds-btn ds-btn--primary" @click="closeLayoutPreview">Close</button>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="fade">
            <base-modal v-if="showBulkUploadModal" title="Bulk Upload Assignments" @close="closeBulkUpload">
                <div class="ds-bulk">
                    <div class="ds-bulk__banner">
                        <div class="ds-bulk__banner-icon">
                            <font-awesome-icon :icon="['fas', 'file-excel']" />
                        </div>
                        <div class="ds-bulk__banner-body">
                            <p class="ds-bulk__banner-title">Upload Assignments</p>
                            <p class="ds-bulk__banner-hint">
                                Entity and campus are automatic. Use the template and keep column headers unchanged.
                            </p>
                        </div>
                    </div>

                    <div class="ds-bulk__config">
                        <div class="ds-form__header-row">
                            <div class="ds-bulk__config-main">
                                <p class="ds-bulk__config-title">Semester configuration</p>
                                <template v-if="showBulkSemesterPicker">
                                    <label class="ds-label mt-2">Semester</label>
                                    <select
                                        v-model="bulkSemester"
                                        class="ds-input"
                                        @change="onBulkSemesterConfigChange"
                                    >
                                        <option value="">Select semester</option>
                                        <option
                                            v-for="sem in formSemesterOptions"
                                            :key="'bulk-config-sem-' + sem"
                                            :value="sem"
                                        >
                                            {{ sem }}
                                        </option>
                                    </select>
                                    <p class="ds-bulk__config-hint">
                                        Choose a semester to preview villa / room / bed layout before uploading.
                                    </p>
                                </template>
                                <p v-else class="ds-bulk__config-sub">
                                    {{ entity }} · {{ campus }} · {{ bulkSemester }}
                                </p>
                            </div>
                            <button
                                v-if="canViewBulkLayout"
                                type="button"
                                class="ds-layout-btn"
                                @click="openLayoutPreview"
                            >
                                <font-awesome-icon :icon="['fas', 'sitemap']" class="mr-2" />
                                View configuration
                            </button>
                        </div>
                        <div v-if="canViewBulkLayout" class="ds-layout-strip">
                            <div class="ds-layout-strip__item">
                                <span class="ds-layout-strip__label">Villas</span>
                                <span class="ds-layout-strip__value">{{ formLayoutSummary.villaCount }}</span>
                            </div>
                            <div class="ds-layout-strip__item">
                                <span class="ds-layout-strip__label">Capacity</span>
                                <span class="ds-layout-strip__value">{{ formLayoutSummary.capacity }}</span>
                            </div>
                            <div class="ds-layout-strip__item">
                                <span class="ds-layout-strip__label">Layout</span>
                                <span class="ds-layout-strip__value">{{ formLayoutSummary.layout }}</span>
                            </div>
                            <div class="ds-layout-strip__item">
                                <span class="ds-layout-strip__label">Identical</span>
                                <span class="ds-layout-strip__value">{{ formLayoutSummary.identical }}</span>
                            </div>
                        </div>
                        <p v-if="canViewBulkLayout && occupancyLine" class="ds-occupancy-line">
                            {{ occupancyLine }}
                        </p>
                        <p
                            v-else-if="bulkSemester && !layoutLoading"
                            class="ds-bulk__config-empty"
                        >
                            No configuration found for semester {{ bulkSemester }}.
                        </p>
                    </div>

                    <div v-if="bulkStep === 'select'" class="ds-bulk__section">
                        <div
                            class="ds-bulk__dropzone"
                            :class="{ 'ds-bulk__dropzone--active': bulkDragOver }"
                            @click="triggerBulkFilePicker"
                            @dragover.prevent="bulkDragOver = true"
                            @dragleave.prevent="bulkDragOver = false"
                            @drop.prevent="onBulkFileDrop"
                        >
                            <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" class="ds-bulk__dropzone-icon" />
                            <p class="ds-bulk__dropzone-title">Drop your Excel file here, or click to browse</p>
                            <p class="ds-bulk__dropzone-sub">Accepted formats: .xlsx, .xls</p>
                        </div>

                        <div class="ds-bulk__tips">
                            <button
                                type="button"
                                class="ds-bulk__link-btn"
                                :disabled="bulkUploading"
                                @click.stop="downloadTemplate"
                            >
                                <font-awesome-icon :icon="['fas', 'download']" class="mr-2" />
                                Download Excel template
                            </button>
                            <p class="ds-bulk__tips-text">
                                Required columns:
                                <strong>Semester</strong>,
                                <strong>Villa</strong>,
                                <strong>Room</strong>,
                                <strong>Bed</strong>, and
                                <strong>Student ID</strong>.
                                Optional columns:
                                <strong>Sponsor</strong> and
                                <strong>Date Of Entry</strong>.
                            </p>
                        </div>
                    </div>

                    <div v-else-if="bulkStep === 'review'" class="ds-bulk__section">
                        <div class="ds-bulk__file-card">
                            <div class="ds-bulk__file-icon">
                                <font-awesome-icon :icon="['fas', 'file-excel']" />
                            </div>
                            <div class="min-w-0 flex-1">
                                <p class="ds-bulk__file-name">{{ bulkFileName }}</p>
                                <p class="ds-bulk__file-meta">
                                    {{ bulkValidRows.length }} valid row{{ bulkValidRows.length === 1 ? "" : "s" }}
                                    <span v-if="bulkValidationErrors.length">
                                        · {{ bulkValidationErrors.length }} issue{{
                                            bulkValidationErrors.length === 1 ? "" : "s"
                                        }}
                                    </span>
                                </p>
                            </div>
                        </div>

                        <div v-if="bulkValidationErrors.length" class="ds-bulk__errors">
                            <div class="ds-bulk__errors-head">
                                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                                Fix the following before upload
                            </div>
                            <div class="ds-bulk__errors-list">
                                <div
                                    v-for="(err, idx) in bulkValidationErrors"
                                    :key="'bulk-err-' + idx"
                                    class="ds-bulk__error-item"
                                >
                                    <span v-if="err.row" class="ds-bulk__error-row">Row {{ err.row }}</span>
                                    <span>{{ err.message }}</span>
                                </div>
                            </div>
                        </div>

                        <div v-else class="ds-bulk__ready">
                            <font-awesome-icon :icon="['fas', 'check-circle']" class="ds-bulk__ready-icon" />
                            <div>
                                <p class="ds-bulk__ready-title">
                                    Ready to upload {{ bulkValidRows.length }} record{{
                                        bulkValidRows.length === 1 ? "" : "s"
                                    }}
                                </p>
                                <p class="ds-bulk__ready-sub">We will create rows for every valid entry.</p>
                            </div>
                        </div>

                        <div class="ds-bulk__footer">
                            <button type="button" class="ds-btn ds-btn--ghost" :disabled="bulkUploading" @click="resetBulk">
                                Change file
                            </button>
                            <button
                                type="button"
                                class="ds-btn ds-btn--primary"
                                :disabled="bulkUploading || !bulkValidRows.length || bulkValidationErrors.length > 0"
                                @click="submitBulkUpload"
                            >
                                Start Upload
                            </button>
                        </div>
                    </div>

                    <div v-else-if="bulkStep === 'uploading'" class="ds-bulk__section">
                        <div class="ds-bulk__progress-card">
                            <div class="ds-bulk__progress-head">
                                <p class="ds-bulk__progress-title">Uploading...</p>
                                <p class="ds-bulk__progress-percent">{{ bulkProgressPercent }}%</p>
                            </div>
                            <div class="ds-bulk__progress-track">
                                <div class="ds-bulk__progress-fill" :style="{ width: bulkProgressPercent + '%' }"></div>
                            </div>
                            <p class="ds-bulk__progress-sub">
                                {{ bulkProgressCurrent }} / {{ bulkProgressTotal }} · {{ bulkProgressPercent }}%
                            </p>
                            <p v-if="bulkCurrentName" class="ds-bulk__progress-name">{{ bulkCurrentName }}</p>
                        </div>
                    </div>

                    <div v-else-if="bulkStep === 'done'" class="ds-bulk__section">
                        <div class="ds-bulk__summary">
                            <div class="ds-bulk__summary-stat ds-bulk__summary-stat--ok">
                                <p class="ds-bulk__summary-value">{{ bulkSuccessCount }}</p>
                                <p class="ds-bulk__summary-label">Successful</p>
                            </div>
                            <div class="ds-bulk__summary-stat ds-bulk__summary-stat--fail">
                                <p class="ds-bulk__summary-value">{{ bulkFailCount }}</p>
                                <p class="ds-bulk__summary-label">Failed</p>
                            </div>
                            <div class="ds-bulk__summary-stat">
                                <p class="ds-bulk__summary-value">{{ bulkProgressTotal }}</p>
                                <p class="ds-bulk__summary-label">Total</p>
                            </div>
                        </div>

                        <div v-if="bulkUploadErrors.length" class="ds-bulk__errors">
                            <div class="ds-bulk__errors-head">
                                <font-awesome-icon :icon="['fas', 'times-circle']" class="mr-2" />
                                Upload errors
                            </div>
                            <div class="ds-bulk__errors-list">
                                <div
                                    v-for="(err, idx) in bulkUploadErrors"
                                    :key="'bulk-up-err-' + idx"
                                    class="ds-bulk__error-item"
                                >
                                    <span v-if="err.row" class="ds-bulk__error-row">Row {{ err.row }}</span>
                                    <span>{{ err.message }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="ds-bulk__footer ds-bulk__footer--single">
                            <button type="button" class="ds-btn ds-btn--primary" @click="closeBulkUpload">Done</button>
                        </div>
                    </div>
                </div>
            </base-modal>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import * as XLSX from "xlsx";
import Pagination from "../../../../components/shared/pagination.vue";
import TableShimmer from "../../../../components/shared/TableShimmer.vue";
import Search from "../../../../components/shared/search.vue";
import exportManagerMixin from "../../facility/modules/exportManagerMixin.js";
import {
    buildAccommodationSemesterLists,
    ACCOMMODATION_SEMESTER_FILTER_ALL,
} from "../../config";

const LIST_QUERY = gql`
    query AccommodationAssignmentsList(
        $first: Int!
        $page: Int!
        $entity: String
        $campus: String
        $semester: String
        $villa: Int
        $search: String
    ) {
        accommodationAssignments(
            first: $first
            page: $page
            entity: $entity
            campus: $campus
            semester: $semester
            villa: $villa
            search: $search
        ) {
            error
            message
            data {
                id
                entity
                campus
                semester
                villa
                room_no
                bed_slot
                student_id
                sponsor
                date_of_entry
                created_by
                student {
                    student_id
                    name_en
                    eid
                    entity
                    campus_code
                    program_name
                }
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const SEMESTERS_QUERY = gql`
    query AccommodationAssignmentSemesters($entity: String!, $campus: String!) {
        accommodationSemesters(entity: $entity, campus: $campus) {
            error
            message
            data
        }
    }
`;

const SETTINGS_QUERY = gql`
    query AccommodationAssignmentSettingsCheck($entity: String!, $campus: String!, $semester: String!) {
        accommodationSettings(entity: $entity, campus: $campus, semester: $semester) {
            error
            message
            data {
                id
                semester
                villas_identical
                villa_count
                total_capacity
                room_bed_layout
                villas {
                    villa_no
                    rooms {
                        room_no
                        bed_count
                        beds {
                            id
                            bed_slot
                            assignment {
                                id
                                student_id
                                sponsor
                                date_of_entry
                                student_name
                                program_name
                            }
                        }
                    }
                }
            }
        }
    }
`;

const OVERVIEW_QUERY = gql`
    query AccommodationAssignmentOverview($entity: String!, $campus: String!, $semester: String) {
        accommodationOverview(entity: $entity, campus: $campus, semester: $semester) {
            error
            message
            data {
                summary {
                    total_capacity
                    available_beds
                    current_occupancy
                    occupied_villas
                }
            }
        }
    }
`;

const SPONSORS_QUERY = gql`
    query AccommodationSponsorsList {
        accommodationSponsors {
            error
            message
            data {
                id
                name
            }
        }
    }
`;

const CREATE_MUTATION = gql`
    mutation CreateAccommodationAssignment($input: AccommodationAssignmentInput!) {
        createAccommodationAssignment(input: $input) {
            error
            message
        }
    }
`;

const UPDATE_MUTATION = gql`
    mutation UpdateAccommodationAssignment($id: ID!, $input: AccommodationAssignmentInput!) {
        updateAccommodationAssignment(id: $id, input: $input) {
            error
            message
        }
    }
`;

const DELETE_MUTATION = gql`
    mutation DeleteAccommodationAssignment($id: ID!) {
        deleteAccommodationAssignment(id: $id) {
            error
            message
        }
    }
`;

const DELETE_BULK_MUTATION = gql`
    mutation DeleteAccommodationAssignmentsBulk($input: DeleteAccommodationAssignmentsBulkInput!) {
        deleteAccommodationAssignmentsBulk(input: $input) {
            error
            message
            data {
                deleted_count
            }
        }
    }
`;

const BULK_MUTATION = gql`
    mutation CreateAccommodationAssignmentsBulk($input: CreateAccommodationAssignmentsBulkInput!) {
        createAccommodationAssignmentsBulk(input: $input) {
            error
            message
            data {
                success_count
                fail_count
            }
        }
    }
`;

function todayDateIso() {
    var d = new Date();
    var y = d.getFullYear();
    var m = d.getMonth() + 1;
    var day = d.getDate();
    return y + "-" + (m < 10 ? "0" : "") + m + "-" + (day < 10 ? "0" : "") + day;
}

var SPONSOR_OTHER = "__other__";

function emptyForm(semester) {
    return {
        semester: semester || "",
        villa: "",
        room_no: "",
        bed_slot: "",
        student_id: "",
        sponsor: "",
        sponsorSelect: "",
        sponsorOther: "",
        date_of_entry: todayDateIso(),
    };
}

function cellToString(value) {
    if (value === null || value === undefined) return "";
    return String(value).replace(/\u00a0/g, " ").trim();
}

export default {
    name: "AccommodationAssignmentsModule",
    mixins: [exportManagerMixin],
    components: { Pagination, TableShimmer, Search },
    props: {
        entity: { type: String, required: true },
        campus: { type: String, required: true },
        semester: { type: String, required: true },
        filterAll: { type: Boolean, default: false },
        initialVilla: { type: [String, Number], default: "" },
    },
        emits: ["changed", "open-configuration"],
    data() {
        return {
            items: [],
            paginatorInfo: null,
            loading: false,
            isExportExcel: false,
            perPage: 25,
            page: 1,
            search: "",
            semesterFilter: "",
            semesterFilterOptions: [{ value: ACCOMMODATION_SEMESTER_FILTER_ALL, label: "All" }],
            formSemesterOptions: [],
            layoutVillas: [],
            formLayoutSetting: null,
            showLayoutPreview: false,
            occupancySummary: null,
            filterVillas: [],
            layoutLoading: false,
            mapLoading: false,
            assignmentsView: "list",
            sponsors: [],
            SPONSOR_OTHER: SPONSOR_OTHER,
            villaFilter: "",
            selectedIds: [],
            deletingSelected: false,
            showFormModal: false,
            editingId: null,
            saving: false,
            form: emptyForm(""),
            showBulkUploadModal: false,
            bulkSemester: "",
            bulkStep: "select",
            bulkFileName: "",
            bulkValidRows: [],
            bulkValidationErrors: [],
            bulkUploadErrors: [],
            bulkUploading: false,
            bulkDragOver: false,
            bulkProgressCurrent: 0,
            bulkProgressTotal: 0,
            bulkCurrentName: "",
            bulkSuccessCount: 0,
            bulkFailCount: 0,
            tableHeaders: ["", "#", "Semester", "Villa", "Room", "Bed", "Student ID", "Name", "Sponsor", "Actions"],
        };
    },
    computed: {
        canCreate() {
            return this.hasPermission("accommodation-assignments-create");
        },
        canUpdate() {
            return this.hasPermission("accommodation-assignments-update");
        },
        canDelete() {
            var perms = this.$store.getters.permissions || [];
            return perms.indexOf("accommodation-assignments-delete") !== -1;
        },
        canManage() {
            return this.canUpdate || this.canDelete;
        },
        bulkProgressPercent() {
            if (!this.bulkProgressTotal) return 0;
            return Math.round((this.bulkProgressCurrent / this.bulkProgressTotal) * 100);
        },
        deletableItems() {
            var self = this;
            return (this.items || []).filter(function (item) {
                return self.canDeleteItem(item);
            });
        },
        allVisibleSelected() {
            var deletable = this.deletableItems;
            if (!deletable || deletable.length === 0) return false;
            for (var i = 0; i < deletable.length; i++) {
                var id = String(deletable[i].id);
                if (this.selectedIds.indexOf(id) === -1) return false;
            }
            return true;
        },
        paginatorInfoWithItems() {
            if (!this.paginatorInfo) return null;
            var pi = this.paginatorInfo;
            return {
                currentPage: pi.currentPage,
                lastPage: pi.lastPage,
                perPage: pi.perPage,
                total: pi.total,
                hasMorePages: pi.hasMorePages,
                firstItem: pi.firstItem || (pi.currentPage - 1) * pi.perPage + 1,
                lastItem: pi.lastItem || Math.min(pi.currentPage * pi.perPage, pi.total),
            };
        },
        showFormSemesterField() {
            return !this.semesterFilter;
        },
        showVillaFilter() {
            return !!(this.semesterFilter || "").trim();
        },
        mapVillas() {
            var villas = this.layoutVillas || [];
            var villaFilter = (this.villaFilter || "").trim();
            if (!villaFilter) return villas;
            var target = parseInt(villaFilter, 10);
            var filtered = [];
            for (var i = 0; i < villas.length; i++) {
                if (parseInt(villas[i].villa_no, 10) === target) {
                    filtered.push(villas[i]);
                }
            }
            return filtered;
        },
        mapStats() {
            var villas = this.mapVillas || [];
            var total = 0;
            var occupied = 0;
            for (var i = 0; i < villas.length; i++) {
                total += this.villaBedTotal(villas[i]);
                occupied += this.villaOccupiedCount(villas[i]);
            }
            return {
                total: total,
                occupied: occupied,
                available: Math.max(0, total - occupied),
                villas: villas.length,
            };
        },
        filterVillaOptions() {
            if (!this.filterVillas || !this.filterVillas.length) return [];
            return this.filterVillas.map(function (v) {
                return v.villa_no;
            });
        },
        hasSponsorOptions() {
            return this.sponsors && this.sponsors.length > 0;
        },
        formLayoutReady() {
            return !!(this.form.semester && this.layoutVillas && this.layoutVillas.length > 0);
        },
        canViewFormLayout() {
            return !!(this.form.semester && this.formLayoutSetting && this.formLayoutSetting.id);
        },
        canViewBulkLayout() {
            return !!(
                this.showBulkUploadModal &&
                this.bulkSemester &&
                this.formLayoutSetting &&
                this.formLayoutSetting.id
            );
        },
        showBulkSemesterPicker() {
            return this.showBulkUploadModal && !(this.semesterFilter || "").trim();
        },
        layoutPreviewSemester() {
            if (this.showFormModal && this.form.semester) return this.form.semester;
            if (this.bulkSemester) return this.bulkSemester;
            if (this.formLayoutSetting && this.formLayoutSetting.semester) {
                return this.formLayoutSetting.semester;
            }
            return "";
        },
        formLayoutSummary() {
            var setting = this.formLayoutSetting || {};
            var villaCount = setting.villa_count;
            if (villaCount == null && this.layoutVillas && this.layoutVillas.length) {
                villaCount = this.layoutVillas.length;
            }
            var capacity = setting.total_capacity;
            if (capacity == null) {
                capacity = 0;
                var villas = this.layoutVillas || [];
                for (var i = 0; i < villas.length; i++) {
                    capacity += this.villaBedCount(villas[i]);
                }
            }
            var layout = "Custom";
            if (setting.villas_identical && setting.room_bed_layout) {
                layout = setting.room_bed_layout;
            } else if (setting.villas_identical === false) {
                layout = "Per villa";
            }
            return {
                villaCount: villaCount || 0,
                capacity: capacity || 0,
                layout: layout,
                identical: setting.villas_identical ? "Yes" : "No",
            };
        },
        occupancyLine() {
            var summary = this.occupancySummary;
            if (!summary) return "";
            var occupied = summary.current_occupancy || 0;
            var capacity = summary.total_capacity || 0;
            var available =
                summary.available_beds != null
                    ? summary.available_beds
                    : Math.max(0, capacity - occupied);
            var villas = summary.occupied_villas || 0;
            return (
                "Occupancy: " +
                occupied +
                " / " +
                capacity +
                " beds · " +
                available +
                " available · " +
                villas +
                " villa" +
                (villas === 1 ? "" : "s") +
                " occupied"
            );
        },
        formVillaOptions() {
            if (!this.layoutVillas || !this.layoutVillas.length) return [];
            return this.layoutVillas.map(function (v) {
                return v.villa_no;
            });
        },
        formRoomOptions() {
            var villa = this.findLayoutVilla(this.form.villa);
            if (!villa || !villa.rooms) return [];
            return villa.rooms.map(function (r) {
                return r.room_no;
            });
        },
        formBedOptions() {
            var room = this.findLayoutRoom(this.form.villa, this.form.room_no);
            if (!room) return [];
            if (room.beds && room.beds.length) {
                return room.beds.map(function (b) {
                    return b.bed_slot;
                });
            }
            var count = room.bed_count || 0;
            var slots = [];
            for (var i = 1; i <= count; i++) slots.push(i);
            return slots;
        },
    },
    watch: {
        semester: function (newVal) {
            this.semesterFilter = newVal || ACCOMMODATION_SEMESTER_FILTER_ALL;
            this.villaFilter = "";
            this.selectedIds = [];
            this.page = 1;
            this.syncFilterVillas().then(this.loadList);
        },
    },
    created() {
        this.semesterFilter = this.semester || "";
        this.villaFilter = this.initialVilla !== null && this.initialVilla !== undefined && this.initialVilla !== ""
            ? String(this.initialVilla)
            : "";
        this.applySemesterLists([]);
        this.loadSemesters();
        this.loadSponsors();
        this.syncFilterVillas().then(this.loadList);
    },
    methods: {
        hasPermission(slug) {
            if (this.$store.getters.role === "superadmin") return true;
            var perms = this.$store.getters.permissions || [];
            return perms.indexOf(slug) !== -1;
        },
        canDeleteItem(item) {
            if (!this.canDelete) return false;
            if (this.$store.getters.role === "superadmin") return true;
            var userId = parseInt(this.$store.getters.userId, 10);
            if (!userId || !item) return false;
            var createdBy = item.created_by != null ? parseInt(item.created_by, 10) : null;
            return createdBy === userId;
        },
        findLayoutVilla(villaNo) {
            if (!villaNo || !this.layoutVillas || !this.layoutVillas.length) return null;
            var target = parseInt(villaNo, 10);
            for (var i = 0; i < this.layoutVillas.length; i++) {
                if (parseInt(this.layoutVillas[i].villa_no, 10) === target) {
                    return this.layoutVillas[i];
                }
            }
            return null;
        },
        findLayoutRoom(villaNo, roomNo) {
            var villa = this.findLayoutVilla(villaNo);
            if (!villa || !villa.rooms || !roomNo) return null;
            var target = parseInt(roomNo, 10);
            for (var i = 0; i < villa.rooms.length; i++) {
                if (parseInt(villa.rooms[i].room_no, 10) === target) {
                    return villa.rooms[i];
                }
            }
            return null;
        },
        roomBedSlots(room) {
            if (!room) return [];
            if (room.beds && room.beds.length) {
                return room.beds.map(function (b) {
                    return b.bed_slot;
                });
            }
            var count = room.bed_count || 0;
            var slots = [];
            for (var i = 1; i <= count; i++) slots.push(i);
            return slots;
        },
        villaBedCount(villa) {
            if (!villa || !villa.rooms) return 0;
            var total = 0;
            for (var i = 0; i < villa.rooms.length; i++) {
                total += this.roomBedSlots(villa.rooms[i]).length;
            }
            return total;
        },
        openLayoutPreview() {
            if (!this.canViewFormLayout && !this.canViewBulkLayout) return;
            this.showLayoutPreview = true;
        },
        closeLayoutPreview() {
            this.showLayoutPreview = false;
        },
        rowNumber(index) {
            var first = this.paginatorInfoWithItems ? this.paginatorInfoWithItems.firstItem : 1;
            return first + index;
        },
        studentName(item) {
            if (item && item.student && item.student.name_en) return item.student.name_en;
            return "—";
        },
        studentEid(item) {
            if (item && item.student && item.student.eid) return item.student.eid;
            return "—";
        },
        studentProgram(item) {
            if (item && item.student && item.student.program_name) return item.student.program_name;
            return "—";
        },
        formatDate(value) {
            if (!value) return "—";
            return String(value).substring(0, 10);
        },
        onSearch(text) {
            this.search = text || "";
            this.page = 1;
            this.selectedIds = [];
            this.loadList();
        },
        setAssignmentsView(view) {
            this.assignmentsView = view === "map" ? "map" : "list";
            if (this.assignmentsView === "map") {
                this.refreshMapView();
            } else {
                this.loadList();
            }
        },
        refreshMapView() {
            var sem = (this.semesterFilter || "").trim();
            if (!sem) {
                this.layoutVillas = [];
                this.formLayoutSetting = null;
                this.mapLoading = false;
                return Promise.resolve();
            }
            this.mapLoading = true;
            var self = this;
            return this.loadLayoutForSemester(sem)
                .then(function () {
                    return self.syncFilterVillas();
                })
                .finally(function () {
                    self.mapLoading = false;
                });
        },
        villaBedTotal(villa) {
            var rooms = villa && villa.rooms ? villa.rooms : [];
            var total = 0;
            for (var i = 0; i < rooms.length; i++) {
                total += rooms[i].beds && rooms[i].beds.length ? rooms[i].beds.length : rooms[i].bed_count || 0;
            }
            return total;
        },
        villaOccupiedCount(villa) {
            var rooms = villa && villa.rooms ? villa.rooms : [];
            var count = 0;
            for (var i = 0; i < rooms.length; i++) {
                count += this.roomOccupiedCount(rooms[i]);
            }
            return count;
        },
        roomOccupiedCount(room) {
            var beds = room && room.beds ? room.beds : [];
            var count = 0;
            for (var i = 0; i < beds.length; i++) {
                if (beds[i].assignment) count++;
            }
            return count;
        },
        villaOccupancyClass(villa) {
            var total = this.villaBedTotal(villa);
            var occupied = this.villaOccupiedCount(villa);
            if (total > 0 && occupied >= total) return "asm-villa__badge--full";
            if (occupied > 0) return "asm-villa__badge--partial";
            return "asm-villa__badge--empty";
        },
        shortStudentLabel(assignment) {
            if (!assignment) return "";
            if (assignment.student_name) {
                var parts = String(assignment.student_name).trim().split(/\s+/);
                if (parts.length === 1) return parts[0].substring(0, 10);
                return (parts[0].substring(0, 1) + ". " + parts[parts.length - 1]).substring(0, 12);
            }
            return assignment.student_id || "";
        },
        bedTooltip(bed) {
            if (!bed) return "";
            if (!bed.assignment) return "Bed " + bed.bed_slot + " · Available — click to assign";
            var a = bed.assignment;
            var lines = ["Bed " + bed.bed_slot + " · Occupied"];
            if (a.student_id) lines.push("ID: " + a.student_id);
            if (a.student_name) lines.push(a.student_name);
            if (a.program_name) lines.push(a.program_name);
            if (a.sponsor) lines.push("Sponsor: " + a.sponsor);
            return lines.join("\n");
        },
        onMapBedClick(villa, room, bed) {
            if (!bed) return;
            if (bed.assignment) {
                if (!this.canUpdate && !this.canDelete) return;
                this.openEdit({
                    id: bed.assignment.id,
                    semester: this.semesterFilter,
                    villa: villa.villa_no,
                    room_no: room.room_no,
                    bed_slot: bed.bed_slot,
                    student_id: bed.assignment.student_id,
                    sponsor: bed.assignment.sponsor,
                    date_of_entry: bed.assignment.date_of_entry,
                    student: {
                        student_id: bed.assignment.student_id,
                        name_en: bed.assignment.student_name,
                        program_name: bed.assignment.program_name,
                    },
                });
                return;
            }
            if (!this.canCreate) return;
            this.openCreateForBed(villa.villa_no, room.room_no, bed.bed_slot);
        },
        openCreateForBed(villaNo, roomNo, bedSlot) {
            var self = this;
            var sem = (this.semesterFilter || "").trim();
            this.loadSponsors().then(function () {
                self.withConfiguredSemester(sem, function () {
                    self.editingId = null;
                    self.form = emptyForm(sem);
                    self.form.villa = String(villaNo);
                    self.form.room_no = String(roomNo);
                    self.form.bed_slot = String(bedSlot);
                    self.showFormModal = true;
                });
            });
        },
        onSemesterFilterChange() {
            this.villaFilter = "";
            this.page = 1;
            this.selectedIds = [];
            if (this.assignmentsView === "map") {
                this.refreshMapView();
            } else {
                this.syncFilterVillas().then(this.loadList);
            }
        },
        onFilterChange() {
            this.page = 1;
            this.selectedIds = [];
            if (this.assignmentsView !== "map") {
                this.loadList();
            }
        },
        toggleSelectAll() {
            if (!this.canDelete) return;
            var ids = this.deletableItems.map(function (item) {
                return String(item.id);
            });
            if (!ids.length) return;
            if (this.allVisibleSelected) {
                this.selectedIds = this.selectedIds.filter(function (id) {
                    return ids.indexOf(id) === -1;
                });
            } else {
                var merged = this.selectedIds.concat(ids);
                var unique = [];
                for (var i = 0; i < merged.length; i++) {
                    if (unique.indexOf(merged[i]) === -1) unique.push(merged[i]);
                }
                this.selectedIds = unique;
            }
        },
        confirmBulkDelete() {
            var self = this;
            if (!this.$swal) return;
            if (!this.selectedIds || this.selectedIds.length === 0) return;
            var semesterLabel = (this.semesterFilter || "").trim() || "All";
            this.$swal
                .fire({
                    title: "Delete selected assignments?",
                    html:
                        "<div class='text-left text-sm text-gray-600'>" +
                        "<div><strong>Semester:</strong> " +
                        semesterLabel +
                        "</div>" +
                        "<div><strong>Records:</strong> " +
                        this.selectedIds.length +
                        "</div>" +
                        "</div>",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#dc2626",
                    cancelButtonColor: "#6b7280",
                    confirmButtonText: "Yes, delete",
                })
                .then(function (result) {
                    if (result && result.isConfirmed) {
                        self.deleteSelected();
                    }
                });
        },
        deleteSelected() {
            var self = this;
            if (!this.selectedIds || this.selectedIds.length === 0) return;
            if (this.deletingSelected) return;
            this.deletingSelected = true;

            var deletingAllOnPage = this.items && this.items.length > 0;
            if (deletingAllOnPage) {
                var allSelected = true;
                for (var i = 0; i < this.items.length; i++) {
                    if (this.selectedIds.indexOf(String(this.items[i].id)) === -1) {
                        allSelected = false;
                        break;
                    }
                }
                if (allSelected && this.page > 1) {
                    this.page = this.page - 1;
                }
            }

            this.$apollo
                .mutate({
                    mutation: DELETE_BULK_MUTATION,
                    variables: {
                        input: {
                            ids: this.selectedIds,
                        },
                    },
                })
                .then(function (resp) {
                    var root = resp.data && resp.data.deleteAccommodationAssignmentsBulk;
                    if (root && root.error) throw new Error(root.message);
                    self.selectedIds = [];
                    self.reloadActiveView();
                    self.$emit("changed");
                    if (self.$swal) {
                        self.$swal.fire({
                            toast: true,
                            position: "top-end",
                            icon: "success",
                            title: (root && root.message) || "Selected records deleted successfully",
                            showConfirmButton: false,
                            timer: 2000,
                        });
                    }
                })
                .catch(function (err) {
                    if (self.$swal) {
                        self.$swal.fire({
                            icon: "error",
                            title: "Error",
                            text: err && err.message ? err.message : "Bulk delete failed.",
                        });
                    }
                    self.selectedIds = [];
                    self.loadList();
                })
                .finally(function () {
                    self.deletingSelected = false;
                });
        },
        syncFilterVillas() {
            var self = this;
            var sem = (this.semesterFilter || "").trim();
            if (!sem) {
                this.filterVillas = [];
                this.villaFilter = "";
                return Promise.resolve();
            }
            return this.$apollo
                .query({
                    query: SETTINGS_QUERY,
                    variables: {
                        entity: this.entity,
                        campus: this.campus,
                        semester: sem,
                    },
                    fetchPolicy: "network-only",
                })
                .then(function (resp) {
                    var root = resp.data && resp.data.accommodationSettings;
                    var data = root && root.data ? root.data : null;
                    self.filterVillas = data && data.villas ? data.villas : [];
                    if (self.villaFilter) {
                        var selected = parseInt(self.villaFilter, 10);
                        var found = false;
                        for (var i = 0; i < self.filterVillas.length; i++) {
                            if (parseInt(self.filterVillas[i].villa_no, 10) === selected) {
                                found = true;
                                break;
                            }
                        }
                        if (!found) self.villaFilter = "";
                    }
                })
                .catch(function () {
                    self.filterVillas = [];
                    self.villaFilter = "";
                });
        },
        setPage(p) {
            this.page = p;
            this.loadList();
        },
        reloadActiveView() {
            if (this.assignmentsView === "map") {
                return this.refreshMapView();
            }
            return this.loadList();
        },
        loadList() {
            this.loading = true;
            var variables = {
                first: this.perPage,
                page: this.page,
                entity: this.entity,
                campus: this.campus,
                semester: this.semesterFilter || null,
                search: this.search || null,
            };
            if (this.showVillaFilter && this.villaFilter) {
                variables.villa = parseInt(this.villaFilter, 10);
            }
            this.$apollo
                .query({ query: LIST_QUERY, variables: variables, fetchPolicy: "network-only" })
                .then((resp) => {
                    var root = resp.data && resp.data.accommodationAssignments;
                    if (root && root.error) throw new Error(root.message);
                    this.items = root && root.data ? root.data : [];
                    this.paginatorInfo = root && root.paginatorInfo ? root.paginatorInfo : null;
                })
                .catch((err) => {
                    this.items = [];
                    if (this.$swal) {
                        this.$swal.fire({ icon: "error", title: "Error", text: err.message || "Failed to load list." });
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        defaultFormSemester() {
            if (this.semesterFilter) return this.semesterFilter;
            if (this.semester) return this.semester;
            if (this.formSemesterOptions && this.formSemesterOptions.length) {
                return this.formSemesterOptions[0];
            }
            return "";
        },
        applySemesterLists(apiSemesters) {
            var lists = buildAccommodationSemesterLists(apiSemesters || []);
            this.semesterFilterOptions = lists.filterSemesters;
            this.formSemesterOptions = lists.formSemesters;
        },
        loadSemesters() {
            if (!this.entity || !this.campus) return;
            this.$apollo
                .query({
                    query: SEMESTERS_QUERY,
                    variables: { entity: this.entity, campus: this.campus },
                    fetchPolicy: "network-only",
                })
                .then((resp) => {
                    var root = resp.data && resp.data.accommodationSemesters;
                    var data = root && root.data ? root.data : [];
                    this.applySemesterLists(data);
                })
                .catch(function () {});
        },
        loadSponsors() {
            var self = this;
            return this.$apollo
                .query({
                    query: SPONSORS_QUERY,
                    fetchPolicy: "network-only",
                })
                .then(function (resp) {
                    var root = resp.data && resp.data.accommodationSponsors;
                    var fromApi = root && !root.error && root.data ? root.data : [];
                    self.sponsors = self.mergeSponsorLists(fromApi, self.sponsorsFromItems());
                })
                .catch(function () {
                    self.sponsors = self.sponsorsFromItems();
                });
        },
        sponsorsFromItems() {
            var seen = {};
            var list = [];
            var items = this.items || [];
            for (var i = 0; i < items.length; i++) {
                var name = items[i] && items[i].sponsor ? String(items[i].sponsor).trim() : "";
                if (!name) continue;
                var key = name.toLowerCase();
                if (seen[key]) continue;
                seen[key] = true;
                list.push({ id: "item-" + key, name: name });
            }
            return list;
        },
        mergeSponsorLists(primary, secondary) {
            var seen = {};
            var list = [];
            var sources = [primary || [], secondary || []];
            for (var s = 0; s < sources.length; s++) {
                var arr = sources[s];
                for (var i = 0; i < arr.length; i++) {
                    var sp = arr[i];
                    var name = sp && sp.name ? String(sp.name).trim() : "";
                    if (!name) continue;
                    var key = name.toLowerCase();
                    if (seen[key]) continue;
                    seen[key] = true;
                    list.push({ id: sp.id || "merged-" + key, name: name });
                }
            }
            list.sort(function (a, b) {
                return String(a.name).localeCompare(String(b.name));
            });
            return list;
        },
        settingsRequiredMessage(semester) {
            if (semester) {
                return "Accommodation is not configured for semester " + semester + ". Save Configuration for this semester before adding assignments.";
            }
            return "Save Configuration for a semester before adding assignments.";
        },
        promptConfigurationRequired(semester) {
            var self = this;
            var go = function () {
                self.showFormModal = false;
                self.showBulkUploadModal = false;
                self.$emit("open-configuration", semester || "");
            };
            if (!this.$swal) {
                go();
                return;
            }
            this.$swal
                .fire({
                    icon: "warning",
                    title: "Configuration required",
                    text: this.settingsRequiredMessage(semester),
                    confirmButtonText: "Open Configuration",
                })
                .then(function () {
                    go();
                });
        },
        loadLayoutForSemester(semester) {
            var self = this;
            if (!semester) {
                this.layoutVillas = [];
                this.formLayoutSetting = null;
                this.occupancySummary = null;
                return Promise.resolve(null);
            }
            this.layoutLoading = true;
            return this.$apollo
                .query({
                    query: SETTINGS_QUERY,
                    variables: {
                        entity: this.entity,
                        campus: this.campus,
                        semester: semester,
                    },
                    fetchPolicy: "network-only",
                })
                .then(function (resp) {
                    var root = resp.data && resp.data.accommodationSettings;
                    var data = root && root.data ? root.data : null;
                    self.layoutVillas = data && data.villas ? data.villas : [];
                    self.formLayoutSetting = data && data.id ? data : null;
                    if (data && data.id) {
                        self.loadOccupancySummary(semester);
                    } else {
                        self.occupancySummary = null;
                    }
                    return data;
                })
                .catch(function (err) {
                    self.layoutVillas = [];
                    self.formLayoutSetting = null;
                    self.occupancySummary = null;
                    throw err;
                })
                .finally(function () {
                    self.layoutLoading = false;
                });
        },
        loadOccupancySummary(semester) {
            var self = this;
            if (!semester) {
                this.occupancySummary = null;
                return Promise.resolve();
            }
            return this.$apollo
                .query({
                    query: OVERVIEW_QUERY,
                    variables: {
                        entity: this.entity,
                        campus: this.campus,
                        semester: semester,
                    },
                    fetchPolicy: "network-only",
                })
                .then(function (resp) {
                    var root = resp.data && resp.data.accommodationOverview;
                    if (root && root.error) {
                        self.occupancySummary = null;
                        return;
                    }
                    var data = root && root.data ? root.data : null;
                    self.occupancySummary = data && data.summary ? data.summary : null;
                })
                .catch(function () {
                    self.occupancySummary = null;
                });
        },
        withConfiguredSemester(semester, onOk) {
            var self = this;
            if (!semester) {
                if (this.$swal) {
                    this.$swal.fire({ icon: "warning", title: "Semester required", text: "Select a semester first." });
                }
                return;
            }
            this.loadLayoutForSemester(semester)
                .then(function (data) {
                    if (!data || !data.id) {
                        self.promptConfigurationRequired(semester);
                        return;
                    }
                    onOk();
                })
                .catch(function (err) {
                    if (self.$swal) {
                        self.$swal.fire({
                            icon: "error",
                            title: "Error",
                            text: err && err.message ? err.message : "Could not verify accommodation settings.",
                        });
                    }
                });
        },
        onFormSemesterChange() {
            this.form.villa = "";
            this.form.room_no = "";
            this.form.bed_slot = "";
            var sem = (this.form.semester || "").trim();
            if (!sem) {
                this.layoutVillas = [];
                this.formLayoutSetting = null;
                this.occupancySummary = null;
                return;
            }
            this.withConfiguredSemester(sem, function () {});
        },
        onFormVillaChange() {
            this.form.room_no = "";
            this.form.bed_slot = "";
        },
        onFormRoomChange() {
            this.form.bed_slot = "";
        },
        onSponsorSelectChange() {
            if (this.form.sponsorSelect !== SPONSOR_OTHER) {
                this.form.sponsorOther = "";
            }
        },
        findDuplicateSponsorName(entered) {
            var value = (entered || "").trim().toLowerCase();
            if (!value || !this.sponsors || !this.sponsors.length) return null;
            for (var i = 0; i < this.sponsors.length; i++) {
                var name = this.sponsors[i].name || "";
                if (name.trim().toLowerCase() === value) {
                    return name;
                }
            }
            return null;
        },
        applySponsorFields(sponsorName) {
            var name = sponsorName || "";
            if (!this.hasSponsorOptions) {
                this.form.sponsor = name;
                this.form.sponsorSelect = "";
                this.form.sponsorOther = "";
                return;
            }
            if (!name) {
                this.form.sponsorSelect = "";
                this.form.sponsorOther = "";
                this.form.sponsor = "";
                return;
            }
            var match = this.findDuplicateSponsorName(name);
            if (match) {
                this.form.sponsorSelect = match;
                this.form.sponsorOther = "";
                this.form.sponsor = match;
                return;
            }
            this.form.sponsorSelect = SPONSOR_OTHER;
            this.form.sponsorOther = name;
            this.form.sponsor = name;
        },
        resolveFormSponsor() {
            if (!this.hasSponsorOptions) {
                return (this.form.sponsor || "").trim();
            }
            if (this.form.sponsorSelect === SPONSOR_OTHER) {
                return (this.form.sponsorOther || "").trim();
            }
            return (this.form.sponsorSelect || "").trim();
        },
        openCreate() {
            var self = this;
            var sem = (this.semesterFilter || "").trim();
            this.loadSponsors().then(function () {
                if (sem) {
                    self.withConfiguredSemester(sem, function () {
                        self.editingId = null;
                        self.form = emptyForm(sem);
                        self.showFormModal = true;
                    });
                    return;
                }
                if (!self.formSemesterOptions || self.formSemesterOptions.length === 0) {
                    self.promptConfigurationRequired("");
                    return;
                }
                self.editingId = null;
                self.layoutVillas = [];
                self.formLayoutSetting = null;
                self.occupancySummary = null;
                self.form = emptyForm("");
                self.showFormModal = true;
            });
        },
        openEdit(item) {
            var self = this;
            var sem = item.semester || this.defaultFormSemester();
            this.loadSponsors().then(function () {
                self.withConfiguredSemester(sem, function () {
                    self.editingId = item.id;
                    self.form = emptyForm(sem);
                    self.form.villa = String(item.villa);
                    self.form.room_no = String(item.room_no);
                    self.form.bed_slot = String(item.bed_slot);
                    self.form.student_id = item.student_id || "";
                    self.form.date_of_entry = item.date_of_entry
                        ? String(item.date_of_entry).substring(0, 10)
                        : todayDateIso();
                    self.applySponsorFields(item.sponsor || "");
                    self.showFormModal = true;
                });
            });
        },
        closeForm() {
            this.showFormModal = false;
            this.editingId = null;
            this.showLayoutPreview = false;
            if (this.assignmentsView !== "map") {
                this.layoutVillas = [];
                this.formLayoutSetting = null;
                this.occupancySummary = null;
            }
            this.form = emptyForm(this.defaultFormSemester());
        },
        buildInput() {
            var sponsor = this.resolveFormSponsor();
            return {
                entity: this.entity,
                campus: this.campus,
                semester: (this.form.semester || "").trim(),
                villa: parseInt(this.form.villa, 10),
                room_no: parseInt(this.form.room_no, 10),
                bed_slot: parseInt(this.form.bed_slot, 10),
                student_id: (this.form.student_id || "").trim(),
                sponsor: sponsor || null,
                date_of_entry: this.form.date_of_entry || null,
            };
        },
        saveForm() {
            var self = this;
            var semester = (this.form.semester || "").trim();
            if (!semester) {
                if (this.$swal) {
                    this.$swal.fire({ icon: "error", title: "Error", text: "Semester is required." });
                }
                return;
            }
            if (!this.form.villa) {
                if (this.$swal) {
                    this.$swal.fire({ icon: "error", title: "Error", text: "Villa is required." });
                }
                return;
            }
            if (!this.form.room_no) {
                if (this.$swal) {
                    this.$swal.fire({ icon: "error", title: "Error", text: "Room number is required." });
                }
                return;
            }
            if (!this.form.bed_slot) {
                if (this.$swal) {
                    this.$swal.fire({ icon: "error", title: "Error", text: "Bed slot is required." });
                }
                return;
            }
            if (!(this.form.student_id || "").trim()) {
                if (this.$swal) {
                    this.$swal.fire({ icon: "error", title: "Error", text: "Student ID is required." });
                }
                return;
            }
            if (this.hasSponsorOptions && this.form.sponsorSelect === SPONSOR_OTHER) {
                var otherName = (this.form.sponsorOther || "").trim();
                if (!otherName) {
                    if (this.$swal) {
                        this.$swal.fire({ icon: "error", title: "Error", text: "Please enter a sponsor name." });
                    }
                    return;
                }
                var duplicate = this.findDuplicateSponsorName(otherName);
                if (duplicate) {
                    if (this.$swal) {
                        this.$swal.fire({
                            icon: "warning",
                            title: "Sponsor already exists",
                            text: 'Sponsor "' + duplicate + '" already exists. Please select it from the list instead of entering it under Other.',
                        });
                    }
                    return;
                }
            }
            this.withConfiguredSemester(semester, function () {
                self.saving = true;
                var input = self.buildInput();
                var mutation = self.editingId ? UPDATE_MUTATION : CREATE_MUTATION;
                var variables = self.editingId ? { id: self.editingId, input: input } : { input: input };
                self.$apollo
                    .mutate({ mutation: mutation, variables: variables })
                    .then(function (resp) {
                        var key = self.editingId ? "updateAccommodationAssignment" : "createAccommodationAssignment";
                        var root = resp.data && resp.data[key];
                        if (root && root.error) throw new Error(root.message);
                        self.closeForm();
                        self.loadSemesters();
                        self.loadSponsors();
                        self.reloadActiveView();
                        self.$emit("changed");
                    })
                    .catch(function (err) {
                        if (self.$swal) {
                            self.$swal.fire({ icon: "error", title: "Error", text: err.message || "Save failed." });
                        }
                    })
                    .finally(function () {
                        self.saving = false;
                    });
            });
        },
        confirmDelete(item) {
            if (!this.$swal) return;
            var self = this;
            this.$swal.fire({
                title: "Delete assignment?",
                text: "Villa " + item.villa + " · Room " + item.room_no,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#dc2626",
                confirmButtonText: "Delete",
            }).then(function (result) {
                if (result.isConfirmed) self.deleteItem(item.id);
            });
        },
        deleteItem(id) {
            var self = this;
            this.$apollo
                .mutate({ mutation: DELETE_MUTATION, variables: { id: id } })
                .then((resp) => {
                    var root = resp.data && resp.data.deleteAccommodationAssignment;
                    if (root && root.error) throw new Error(root.message);
                    self.selectedIds = self.selectedIds.filter(function (selectedId) {
                        return selectedId !== String(id);
                    });
                    self.reloadActiveView();
                    self.$emit("changed");
                    if (self.$swal) {
                        self.$swal.fire({
                            toast: true,
                            position: "top-end",
                            icon: "success",
                            title: (root && root.message) || "Record deleted successfully",
                            showConfirmButton: false,
                            timer: 2000,
                        });
                    }
                })
                .catch((err) => {
                    if (this.$swal) {
                        this.$swal.fire({ icon: "error", title: "Error", text: err.message || "Delete failed." });
                    }
                });
        },
        downloadTemplate() {
            var semester = (this.semesterFilter || "").trim();
            if (!semester) {
                this.downloadStaticTemplate();
                return;
            }
            var self = this;
            this.loadLayoutForSemester(semester)
                .then(function (data) {
                    if (!data || !data.id) {
                        self.promptConfigurationRequired(semester);
                        return;
                    }
                    var villas = data.villas || [];
                    if (!villas.length) {
                        if (self.$swal) {
                            self.$swal.fire({
                                icon: "warning",
                                title: "No layout found",
                                text: "Configuration for semester " + semester + " has no villa/room/bed layout.",
                            });
                        }
                        return;
                    }
                    self.downloadConfiguredTemplate(semester, villas);
                })
                .catch(function (err) {
                    if (self.$swal) {
                        self.$swal.fire({
                            icon: "error",
                            title: "Error",
                            text: err && err.message ? err.message : "Failed to build template.",
                        });
                    }
                });
        },
        downloadStaticTemplate() {
            try {
                var link = document.createElement("a");
                link.href = require("../../../../assets/templates/adpoly-accommodation-bulk-upload-template.xlsx");
                link.download = "Accommodation.xlsx";
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (e) {
                if (this.$swal) {
                    this.$swal.fire({ icon: "error", title: "Error", text: "Failed to download template." });
                }
            }
        },
        buildTemplateRowsFromLayout(semester, villas) {
            var rows = [["Semester", "Villa", "Room", "Bed", "Student ID", "Sponsor", "Date Of Entry"]];
            for (var v = 0; v < villas.length; v++) {
                var villa = villas[v];
                var villaNo = villa && villa.villa_no != null ? villa.villa_no : null;
                var rooms = villa && villa.rooms ? villa.rooms : [];
                for (var r = 0; r < rooms.length; r++) {
                    var room = rooms[r];
                    var roomNo = room && room.room_no != null ? room.room_no : null;
                    var beds = room && room.beds ? room.beds : [];
                    if (beds.length) {
                        for (var b = 0; b < beds.length; b++) {
                            var bedSlot = beds[b] && beds[b].bed_slot != null ? beds[b].bed_slot : null;
                            if (villaNo == null || roomNo == null || bedSlot == null) continue;
                            rows.push([semester, villaNo, roomNo, bedSlot, "", "", ""]);
                        }
                    } else {
                        var count = room && room.bed_count ? parseInt(room.bed_count, 10) : 0;
                        for (var slot = 1; slot <= count; slot++) {
                            if (villaNo == null || roomNo == null) continue;
                            rows.push([semester, villaNo, roomNo, slot, "", "", ""]);
                        }
                    }
                }
            }
            return rows;
        },
        downloadConfiguredTemplate(semester, villas) {
            try {
                var rows = this.buildTemplateRowsFromLayout(semester, villas);
                if (rows.length <= 1) {
                    if (this.$swal) {
                        this.$swal.fire({
                            icon: "warning",
                            title: "No beds found",
                            text: "Could not find villa/room/bed slots for semester " + semester + ".",
                        });
                    }
                    return;
                }
                var workbook = XLSX.utils.book_new();
                var sheet = XLSX.utils.aoa_to_sheet(rows);
                sheet["!cols"] = [
                    { wch: 12 },
                    { wch: 8 },
                    { wch: 8 },
                    { wch: 8 },
                    { wch: 14 },
                    { wch: 16 },
                    { wch: 14 },
                ];
                XLSX.utils.book_append_sheet(workbook, sheet, "Assignments");
                var filename = "Accommodation_" + semester + ".xlsx";
                XLSX.writeFile(workbook, filename);
            } catch (e) {
                if (this.$swal) {
                    this.$swal.fire({
                        icon: "error",
                        title: "Error",
                        text: e && e.message ? e.message : "Failed to download template.",
                    });
                }
            }
        },
        openBulkUpload() {
            var self = this;
            if (!this.formSemesterOptions || this.formSemesterOptions.length === 0) {
                this.promptConfigurationRequired("");
                return;
            }
            this.showBulkUploadModal = true;
            this.bulkSemester = (this.semesterFilter || this.semester || "").trim();
            this.bulkStep = "select";
            this.bulkFileName = "";
            this.bulkValidRows = [];
            this.bulkValidationErrors = [];
            this.bulkUploadErrors = [];
            this.bulkDragOver = false;
            this.bulkProgressCurrent = 0;
            this.bulkProgressTotal = 0;
            this.bulkCurrentName = "";
            this.bulkSuccessCount = 0;
            this.bulkFailCount = 0;
            this.bulkUploading = false;
            this.showLayoutPreview = false;
            if (this.bulkSemester) {
                this.loadLayoutForSemester(this.bulkSemester).catch(function () {
                    self.formLayoutSetting = null;
                    self.layoutVillas = [];
                    self.occupancySummary = null;
                });
            } else {
                this.formLayoutSetting = null;
                this.layoutVillas = [];
                this.occupancySummary = null;
            }
        },
        onBulkSemesterConfigChange() {
            var self = this;
            var sem = (this.bulkSemester || "").trim();
            this.showLayoutPreview = false;
            if (!sem) {
                this.formLayoutSetting = null;
                this.layoutVillas = [];
                this.occupancySummary = null;
                return;
            }
            this.loadLayoutForSemester(sem).catch(function () {
                self.formLayoutSetting = null;
                self.layoutVillas = [];
                self.occupancySummary = null;
            });
        },
        closeBulkUpload() {
            this.showBulkUploadModal = false;
            this.bulkDragOver = false;
            this.showLayoutPreview = false;
            if (!this.showFormModal && this.assignmentsView !== "map") {
                this.formLayoutSetting = null;
                this.layoutVillas = [];
                this.occupancySummary = null;
            }
            if (this.bulkSuccessCount > 0) {
                this.reloadActiveView();
                this.loadSponsors();
                this.$emit("changed");
            }
        },
        triggerBulkFilePicker() {
            if (this.$refs.bulkFileInput) this.$refs.bulkFileInput.click();
        },
        resetBulk() {
            this.bulkStep = "select";
            this.bulkFileName = "";
            this.bulkValidRows = [];
            this.bulkValidationErrors = [];
            this.bulkUploadErrors = [];
            this.bulkDragOver = false;
            this.bulkProgressCurrent = 0;
            this.bulkProgressTotal = 0;
            this.bulkCurrentName = "";
            if (this.$refs.bulkFileInput) this.$refs.bulkFileInput.value = "";
            if (this.bulkSemester) {
                this.loadLayoutForSemester(this.bulkSemester).catch(function () {});
            }
        },
        onBulkFileDrop(event) {
            this.bulkDragOver = false;
            if (!event || !event.dataTransfer || !event.dataTransfer.files) return;
            var files = event.dataTransfer.files;
            if (!files.length) return;
            this.onBulkFileSelected({ target: { files: files } });
        },
        onBulkFileSelected(evt) {
            var file = evt.target.files && evt.target.files[0];
            if (!file) return;
            this.bulkFileName = file.name;
            var reader = new FileReader();
            var self = this;
            reader.onload = function (e) {
                self.parseBulkWorkbook(e.target.result);
            };
            reader.readAsArrayBuffer(file);
        },
        parseBulkWorkbook(buffer) {
            try {
                var workbook = XLSX.read(buffer, { type: "array" });
                var sheetName = workbook.SheetNames[0];
                var sheet = workbook.Sheets[sheetName];
                var rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
                this.processBulkRows(rows);
            } catch (err) {
                if (this.$swal) {
                    this.$swal.fire({ icon: "error", title: "Parse error", text: err.message || "Invalid Excel file." });
                }
            }
        },
        processBulkRows(rows) {
            var valid = [];
            var errors = [];
            var headerRow = null;
            var colMap = {};
            var defaultSemester = (this.bulkSemester || this.semesterFilter || "").trim();

            for (var r = 0; r < rows.length; r++) {
                var row = rows[r];
                if (!row || row.length === 0) continue;

                if (headerRow === null) {
                    var maybeHeader = false;
                    for (var c = 0; c < row.length; c++) {
                        var h = cellToString(row[c]).toLowerCase();
                        if (
                            h === "villa" ||
                            h === "room" ||
                            h === "room no." ||
                            h === "room no" ||
                            h === "student id" ||
                            h === "semester"
                        ) {
                            maybeHeader = true;
                            break;
                        }
                    }
                    if (maybeHeader) {
                        headerRow = r;
                        for (var hc = 0; hc < row.length; hc++) {
                            var label = cellToString(row[hc]).toLowerCase();
                            if (label === "semester") colMap.semester = hc;
                            if (label === "villa") colMap.villa = hc;
                            if (label === "room" || label === "room no." || label === "room no") colMap.room = hc;
                            if (label === "bed" || label === "bed slot") colMap.bed = hc;
                            if (label === "student id") colMap.student = hc;
                            if (label === "sponser" || label === "sponsor") colMap.sponsor = hc;
                            if (label === "date of entry") colMap.date = hc;
                        }
                        continue;
                    }
                }

                if (headerRow === null) continue;

                var excelRow = r + 1;
                var semesterRaw = colMap.semester !== undefined ? cellToString(row[colMap.semester]) : "";
                var villaRaw = colMap.villa !== undefined ? cellToString(row[colMap.villa]) : "";
                var roomRaw = colMap.room !== undefined ? cellToString(row[colMap.room]) : "";
                var bedRaw = colMap.bed !== undefined ? cellToString(row[colMap.bed]) : "";
                var studentId = colMap.student !== undefined ? cellToString(row[colMap.student]) : "";
                var sponsor = colMap.sponsor !== undefined ? cellToString(row[colMap.sponsor]) : "";
                var dateVal = colMap.date !== undefined ? cellToString(row[colMap.date]) : "";

                if (!semesterRaw && !villaRaw && !roomRaw && !bedRaw && !studentId) continue;

                var semester = (semesterRaw || defaultSemester).trim();
                var villa = parseInt(villaRaw, 10);
                var roomNo = parseInt(roomRaw, 10);
                var bedSlot = parseInt(bedRaw, 10);

                if (!semester) {
                    errors.push({ row: excelRow, message: "Semester is required." });
                    continue;
                }
                if (!villa || !roomNo) {
                    errors.push({ row: excelRow, message: "Villa and room are required." });
                    continue;
                }
                if (!bedSlot) {
                    errors.push({ row: excelRow, message: "Bed is required." });
                    continue;
                }
                if (!studentId) {
                    errors.push({ row: excelRow, message: "Student ID is required." });
                    continue;
                }

                valid.push({
                    row: excelRow,
                    semester: semester,
                    villa: villa,
                    room_no: roomNo,
                    bed_slot: bedSlot,
                    student_id: studentId,
                    sponsor: sponsor || null,
                    date_of_entry: dateVal || null,
                });
            }

            this.bulkValidRows = valid;
            this.bulkValidationErrors = errors;
            this.bulkStep = "review";

            if (!this.bulkSemester && valid.length) {
                var semesterMap = {};
                for (var si = 0; si < valid.length; si++) {
                    if (valid[si].semester) semesterMap[valid[si].semester] = true;
                }
                var uniqueSemesters = Object.keys(semesterMap);
                if (uniqueSemesters.length === 1) {
                    this.bulkSemester = uniqueSemesters[0];
                    this.loadLayoutForSemester(uniqueSemesters[0]).catch(function () {});
                }
            } else if (this.bulkSemester && (!this.formLayoutSetting || !this.formLayoutSetting.id)) {
                this.loadLayoutForSemester(this.bulkSemester).catch(function () {});
            }
        },
        submitBulkUpload() {
            if (!this.bulkValidRows.length || this.bulkUploading) return;
            if (this.bulkValidationErrors.length) return;
            var self = this;
            var bySemester = {};
            for (var i = 0; i < this.bulkValidRows.length; i++) {
                var item = this.bulkValidRows[i];
                var sem = item.semester;
                if (!bySemester[sem]) bySemester[sem] = [];
                bySemester[sem].push({
                    row: item.row,
                    villa: item.villa,
                    room_no: item.room_no,
                    bed_slot: item.bed_slot,
                    student_id: item.student_id,
                    sponsor: item.sponsor,
                    date_of_entry: item.date_of_entry,
                });
            }
            var semesterKeys = Object.keys(bySemester);
            if (!semesterKeys.length) return;

            this.bulkUploading = true;
            this.bulkStep = "uploading";
            this.bulkUploadErrors = [];
            this.bulkProgressTotal = this.bulkValidRows.length;
            this.bulkProgressCurrent = 0;
            this.bulkCurrentName = "";
            this.bulkSuccessCount = 0;
            this.bulkFailCount = 0;

            this.runBulkBySemester(semesterKeys, bySemester, 200)
                .then(function (counts) {
                    self.bulkUploading = false;
                    self.bulkSuccessCount = counts.success;
                    self.bulkFailCount = counts.fail;
                    self.bulkProgressCurrent = self.bulkProgressTotal;
                    self.bulkStep = "done";
                })
                .catch(function (err) {
                    self.bulkUploading = false;
                    self.bulkStep = "done";
                    if (self.$swal) {
                        self.$swal.fire({
                            icon: "error",
                            title: "Error",
                            text: err && err.message ? err.message : "Bulk upload failed.",
                        });
                    }
                });
        },
        runBulkBySemester(semesterKeys, bySemester, chunkSize) {
            var self = this;
            var success = 0;
            var fail = 0;

            function next(index) {
                if (index >= semesterKeys.length) {
                    return Promise.resolve({ success: success, fail: fail });
                }
                var semester = semesterKeys[index];
                var rows = bySemester[semester];
                self.bulkCurrentName = "Semester " + semester;
                return self.ensureSemesterConfigured(semester).then(function (ok) {
                    if (!ok) {
                        fail += rows.length;
                        self.bulkProgressCurrent += rows.length;
                        self.bulkUploadErrors.push({
                            row: null,
                            message: "Configuration missing for semester " + semester + " (" + rows.length + " row(s) skipped).",
                        });
                        return next(index + 1);
                    }
                    return self.uploadBulkChunks(semester, rows, chunkSize).then(function (counts) {
                        success += counts.success;
                        fail += counts.fail;
                        return next(index + 1);
                    });
                });
            }

            return next(0);
        },
        ensureSemesterConfigured(semester) {
            return this.loadLayoutForSemester(semester).then(function (data) {
                if (!data || !data.id) {
                    return false;
                }
                return true;
            });
        },
        uploadBulkChunks(semester, rows, chunkSize) {
            var self = this;
            var chunks = [];
            for (var i = 0; i < rows.length; i += chunkSize) {
                chunks.push(rows.slice(i, i + chunkSize));
            }
            var success = 0;
            var fail = 0;

            function uploadChunk(index) {
                if (index >= chunks.length) {
                    return Promise.resolve({ success: success, fail: fail });
                }
                var chunk = chunks[index];
                self.bulkCurrentName = "Semester " + semester + " · batch " + (index + 1);
                return self.$apollo
                    .mutate({
                        mutation: BULK_MUTATION,
                        variables: {
                            input: {
                                entity: self.entity,
                                campus: self.campus,
                                semester: semester,
                                rows: chunk,
                            },
                        },
                    })
                    .then(function (resp) {
                        var root = resp.data && resp.data.createAccommodationAssignmentsBulk;
                        if (root && root.error) throw new Error(root.message);
                        if (root && root.data) {
                            success += root.data.success_count || 0;
                            fail += root.data.fail_count || 0;
                            if (root.data.results && root.data.results.length) {
                                for (var r = 0; r < root.data.results.length; r++) {
                                    var result = root.data.results[r];
                                    if (result && !result.success) {
                                        self.bulkUploadErrors.push({
                                            row: result.row,
                                            message: result.message || "Upload failed.",
                                        });
                                    }
                                }
                            }
                        }
                        self.bulkProgressCurrent += chunk.length;
                        return uploadChunk(index + 1);
                    })
                    .catch(function (err) {
                        fail += chunk.length;
                        self.bulkProgressCurrent += chunk.length;
                        self.bulkUploadErrors.push({
                            row: null,
                            message:
                                "Semester " +
                                semester +
                                ": " +
                                (err && err.message ? err.message : "Upload failed."),
                        });
                        return uploadChunk(index + 1);
                    });
            }

            return uploadChunk(0);
        },
        exportFileName() {
            return "ADPOLY_" + (this.campus || "ALL") + "_Accommodation_" + (this.semesterFilter || "ALL");
        },
        exportSheetName() {
            return "Assignments";
        },
        _getExportQuery: function () {
            return { query: LIST_QUERY, root: "accommodationAssignments" };
        },
        _snapshotExportVariables: function () {
            var entity = this.entity;
            var campus = this.campus;
            var semester = this.semesterFilter || null;
            var search = this.search || null;
            var villaFilter = this.villaFilter || "";
            var showVilla = !!(semester || "").trim();
            return function (page, limit) {
                var vars = {
                    first: limit,
                    page: page,
                    entity: entity,
                    campus: campus,
                    semester: semester,
                    search: search,
                };
                if (showVilla && villaFilter) {
                    vars.villa = parseInt(villaFilter, 10);
                }
                return vars;
            };
        },
        mapExportRow(item) {
            return {
                Entity: item.entity || this.entity || "",
                Campus: item.campus || this.campus || "",
                Semester: item.semester || this.semesterFilter || this.semester || "",
                Villa: item.villa,
                "Room no.": item.room_no,
                "Bed Slot": item.bed_slot,
                "Student ID": item.student_id || "",
                "Student Name": this.studentName(item) === "—" ? "" : this.studentName(item),
                "Emirates ID": this.studentEid(item) === "—" ? "" : this.studentEid(item),
                Program: this.studentProgram(item) === "—" ? "" : this.studentProgram(item),
                Sponser: item.sponsor || "",
                "Date Of Entry": item.date_of_entry || "",
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.ds-wrap {
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 40%);
}
.ds-toolbar {
    padding: 1.25rem 1rem 1rem;
}
@media (min-width: 640px) {
    .ds-toolbar {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}
.ds-toolbar__row {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
@media (min-width: 1024px) {
    .ds-toolbar__row {
        flex-direction: row;
        align-items: flex-end;
        justify-content: space-between;
    }
}
.ds-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: flex-end;
}
.ds-search {
    min-width: 14rem;
}
.ds-filter__label {
    display: block;
    font-size: 0.65rem;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 0.25rem;
    text-transform: uppercase;
}
.ds-select {
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    padding: 0.45rem 0.65rem;
    font-size: 0.875rem;
    background: #fff;
}
.ds-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}
.ds-export-btn,
.ds-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0.85rem;
    border-radius: 0.5rem;
    font-size: 0.8125rem;
    font-weight: 600;
}
.ds-export-btn {
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #334155;
}
.ds-btn--ghost {
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #475569;
}
.ds-btn--primary {
    background: linear-gradient(180deg, #c2410c 0%, #ea580c 100%);
    color: #fff;
    border: none;
}
.ds-btn--danger {
    background: #dc2626;
    color: #ffffff;
    border: 1px solid #dc2626;
}
.ds-btn--danger:hover {
    background: #b91c1c;
}
.ds-btn--danger:disabled {
    pointer-events: none;
    opacity: 0.6;
}
.ds-checkbox {
    width: 1rem;
    height: 1rem;
    accent-color: #ea580c;
}
.ds-table-shell {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
}
.ds-th {
    padding: 0.75rem 1rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #64748b;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}
.ds-td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
}
.ds-row:last-child .ds-td {
    border-bottom: none;
}
.ds-icon-btn {
    width: 2rem;
    height: 2rem;
    border-radius: 0.375rem;
    border: 1px solid #e2e8f0;
    background: #fff;
    margin-left: 0.25rem;
}
.ds-icon-btn--danger:hover {
    border-color: #fecaca;
    background: #fef2f2;
}
.ds-form__header,
.ds-form__body,
.ds-form__footer {
    padding: 1rem 1.25rem;
}
.ds-form__header-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
}
.ds-layout-btn {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    border-radius: 0.5rem;
    border: 1px solid #fdba74;
    background: #fff7ed;
    color: #c2410c;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.45rem 0.75rem;
    cursor: pointer;
}
.ds-layout-btn:hover {
    background: #ffedd5;
}
.ds-layout-strip {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
    margin-top: 0.85rem;
}
@media (min-width: 640px) {
    .ds-layout-strip {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}
.ds-layout-strip__item {
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    border-radius: 0.65rem;
    padding: 0.55rem 0.7rem;
}
.ds-layout-strip__label {
    display: block;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #94a3b8;
    margin-bottom: 0.15rem;
}
.ds-layout-strip__value {
    display: block;
    font-size: 0.85rem;
    font-weight: 800;
    color: #334155;
}
.ds-occupancy-line {
    margin: 0.75rem 0 0;
    padding: 0.55rem 0.75rem;
    border-radius: 0.65rem;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #475569;
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.4;
}
.ds-layout-preview {
    position: fixed;
    inset: 0;
    z-index: 10100;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.45);
    padding: 1rem;
}
.ds-layout-preview__panel {
    width: 100%;
    max-width: 42rem;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 1rem;
    box-shadow: 0 20px 40px -20px rgba(15, 23, 42, 0.45);
}
.ds-layout-preview__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1.1rem 1.25rem;
    border-bottom: 1px solid #e2e8f0;
}
.ds-layout-preview__title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 800;
    color: #0f172a;
}
.ds-layout-preview__sub {
    margin: 0.3rem 0 0;
    font-size: 0.85rem;
    color: #64748b;
}
.ds-layout-preview__close {
    width: 2rem;
    height: 2rem;
    border-radius: 9999px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #64748b;
    cursor: pointer;
}
.ds-layout-preview__stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.65rem;
    padding: 1rem 1.25rem 0;
}
@media (min-width: 640px) {
    .ds-layout-preview__stats {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}
.ds-layout-preview__stat {
    border: 1px solid #ffedd5;
    background: #fff7ed;
    border-radius: 0.75rem;
    padding: 0.7rem 0.8rem;
}
.ds-layout-preview__stat-value {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 900;
    color: #c2410c;
    word-break: break-word;
}
.ds-layout-preview__stat-label {
    margin: 0.2rem 0 0;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #9a3412;
}
.ds-layout-preview__body {
    padding: 1rem 1.25rem;
    overflow-y: auto;
    flex: 1 1 auto;
}
.ds-layout-villa {
    border: 1px solid #e2e8f0;
    border-radius: 0.85rem;
    margin-bottom: 0.75rem;
    overflow: hidden;
}
.ds-layout-villa__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.7rem 0.9rem;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}
.ds-layout-villa__title {
    font-size: 0.9rem;
    font-weight: 800;
    color: #0f172a;
}
.ds-layout-villa__meta {
    font-size: 0.75rem;
    font-weight: 700;
    color: #64748b;
}
.ds-layout-rooms {
    padding: 0.75rem 0.9rem;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
}
.ds-layout-room__title {
    font-size: 0.8rem;
    font-weight: 700;
    color: #475569;
    margin-bottom: 0.35rem;
}
.ds-layout-beds {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}
.ds-layout-bed {
    display: inline-flex;
    align-items: center;
    border-radius: 0.45rem;
    border: 1px solid #fed7aa;
    background: #fff7ed;
    color: #9a3412;
    font-size: 0.72rem;
    font-weight: 700;
    padding: 0.25rem 0.5rem;
}
.ds-layout-preview__footer {
    display: flex;
    justify-content: flex-end;
    padding: 0.85rem 1.25rem 1.1rem;
    border-top: 1px solid #e2e8f0;
}
.ds-form__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    border-top: 1px solid #f1f5f9;
}
.ds-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 0.35rem;
}
.ds-input {
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
}
.ds-empty-icon {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
}
.hidden {
    display: none;
}
.ds-bulk {
    padding: 1rem 2rem;
}
.ds-bulk__banner {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    background: #ecfeff;
    border: 1px solid rgba(15, 118, 110, 0.18);
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
    margin-bottom: 1rem;
}
.ds-bulk__banner-icon {
    font-size: 1.25rem;
    color: #0f766e;
    line-height: 1;
}
.ds-bulk__banner-title {
    font-size: 1rem;
    font-weight: 800;
    color: #0f766e;
    margin: 0;
}
.ds-bulk__banner-hint {
    margin: 0.35rem 0 0;
    font-size: 0.85rem;
    color: #4b5563;
    line-height: 1.35;
}
.ds-bulk__config {
    margin: 0 0 1rem;
    padding: 0.9rem 1rem;
    border: 1px solid #ffedd5;
    background: #fffbeb;
    border-radius: 0.85rem;
}
.ds-bulk__config-main {
    flex: 1 1 auto;
    min-width: 0;
}
.ds-bulk__config-title {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 800;
    color: #9a3412;
}
.ds-bulk__config-sub {
    margin: 0.25rem 0 0;
    font-size: 0.8rem;
    color: #b45309;
}
.ds-bulk__config-hint {
    margin: 0.4rem 0 0;
    font-size: 0.78rem;
    color: #b45309;
    line-height: 1.35;
}
.ds-bulk__config-empty {
    margin: 0.75rem 0 0;
    font-size: 0.82rem;
    color: #b45309;
}
.ds-bulk__section {
    padding: 0 0.25rem 0.5rem;
}
.ds-bulk__dropzone {
    border: 2px dashed rgba(15, 118, 110, 0.35);
    background: #f8fafc;
    border-radius: 0.85rem;
    padding: 1.5rem 1rem;
    text-align: center;
    cursor: pointer;
}
.ds-bulk__dropzone--active {
    border-color: rgba(15, 118, 110, 0.8);
    background: #ecfeff;
}
.ds-bulk__dropzone-icon {
    font-size: 1.5rem;
    color: #0f766e;
}
.ds-bulk__dropzone-title {
    margin: 0.75rem 0 0.25rem;
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f766e;
}
.ds-bulk__dropzone-sub {
    margin: 0;
    font-size: 0.82rem;
    color: #6b7280;
}
.ds-bulk__tips {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}
.ds-bulk__link-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border-radius: 0.75rem;
    border: 1px solid rgba(59, 130, 246, 0.25);
    background: rgba(59, 130, 246, 0.06);
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
    font-weight: 700;
    color: #1d4ed8;
    cursor: pointer;
}
.ds-bulk__link-btn:hover {
    background: rgba(59, 130, 246, 0.12);
    border-color: rgba(59, 130, 246, 0.45);
}
.ds-bulk__tips-text {
    font-size: 0.85rem;
    color: #4b5563;
    line-height: 1.4;
}
.ds-bulk__file-card {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    border-radius: 0.9rem;
    padding: 0.85rem 1rem;
    margin-bottom: 1rem;
}
.ds-bulk__file-icon {
    color: #0f766e;
    font-size: 1.1rem;
}
.ds-bulk__file-name {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 800;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.ds-bulk__file-meta {
    margin: 0.25rem 0 0;
    font-size: 0.82rem;
    color: #6b7280;
}
.ds-bulk__errors {
    margin-top: 0.75rem;
    border: 1px solid rgba(239, 68, 68, 0.2);
    background: rgba(254, 242, 242, 0.7);
    border-radius: 0.9rem;
    padding: 1rem;
}
.ds-bulk__errors-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 800;
    color: #b91c1c;
    margin-bottom: 0.75rem;
}
.ds-bulk__errors-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}
.ds-bulk__error-item {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: baseline;
    color: #7f1d1d;
    font-size: 0.85rem;
}
.ds-bulk__error-row {
    font-weight: 800;
}
.ds-bulk__ready {
    margin-top: 1rem;
    display: flex;
    gap: 0.85rem;
    align-items: flex-start;
    background: rgba(16, 185, 129, 0.08);
    border: 1px solid rgba(16, 185, 129, 0.22);
    border-radius: 0.9rem;
    padding: 0.9rem 1rem;
}
.ds-bulk__ready-icon {
    margin-top: 0.1rem;
    color: #047857;
    font-size: 1.25rem;
}
.ds-bulk__ready-title {
    margin: 0;
    font-weight: 900;
    color: #047857;
    line-height: 1.25;
}
.ds-bulk__ready-sub {
    margin: 0.35rem 0 0;
    color: #065f46;
    font-size: 0.85rem;
}
.ds-bulk__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1rem;
}
.ds-bulk__footer--single {
    justify-content: flex-end;
}
.ds-bulk__progress-card {
    border: 1px solid #e5e7eb;
    background: #ffffff;
    border-radius: 0.9rem;
    padding: 1rem;
}
.ds-bulk__progress-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}
.ds-bulk__progress-title {
    font-weight: 800;
    color: #111827;
}
.ds-bulk__progress-percent {
    font-weight: 900;
    color: #0f766e;
}
.ds-bulk__progress-track {
    height: 10px;
    background: #f3f4f6;
    border-radius: 9999px;
    overflow: hidden;
}
.ds-bulk__progress-fill {
    height: 10px;
    background: #0f766e;
    border-radius: 9999px;
}
.ds-bulk__progress-sub {
    margin: 0.75rem 0 0;
    color: #6b7280;
    font-size: 0.85rem;
}
.ds-bulk__progress-name {
    margin: 0.5rem 0 0;
    color: #374151;
    font-size: 0.85rem;
    font-weight: 700;
}
.ds-bulk__summary {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.75rem;
    margin-bottom: 1rem;
}
.ds-bulk__summary-stat {
    border: 1px solid #e5e7eb;
    background: #ffffff;
    border-radius: 0.9rem;
    padding: 0.85rem 1rem;
}
.ds-bulk__summary-stat--ok {
    border-color: rgba(16, 185, 129, 0.3);
    background: rgba(16, 185, 129, 0.08);
}
.ds-bulk__summary-stat--fail {
    border-color: rgba(239, 68, 68, 0.3);
    background: rgba(239, 68, 68, 0.08);
}
.ds-bulk__summary-stat--ok .ds-bulk__summary-value {
    color: #047857;
}
.ds-bulk__summary-stat--fail .ds-bulk__summary-value {
    color: #b91c1c;
}
.ds-bulk__summary-value {
    font-size: 1.6rem;
    font-weight: 900;
    margin: 0;
    color: #111827;
}
.ds-bulk__summary-label {
    margin: 0.25rem 0 0;
    color: #6b7280;
    font-size: 0.85rem;
    font-weight: 700;
}

.asm-tabs {
    display: flex;
    align-items: stretch;
    gap: 0.15rem;
    margin: 0 0 1.25rem;
    border-bottom: 1px solid #e2e8f0;
}
.asm-tabs__tab {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.7rem 1rem;
    border: 0;
    background: transparent;
    color: #64748b;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: -1px;
}
.asm-tabs__tab:hover {
    color: #0f172a;
}
.asm-tabs__tab--active {
    color: #c2410c;
}
.asm-tabs__tab--active::after {
    content: "";
    position: absolute;
    left: 0.65rem;
    right: 0.65rem;
    bottom: 0;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: #ea580c;
}
.asm-tabs__icon {
    font-size: 0.8rem;
}
.asm-map-empty-icon {
    width: 3.5rem;
    height: 3.5rem;
    margin: 0 auto 1rem;
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff7ed;
    color: #ea580c;
    font-size: 1.35rem;
}
.asm-map__legend-bar {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    margin-bottom: 1.25rem;
    padding: 1rem 1.1rem;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 1rem;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
@media (min-width: 768px) {
    .asm-map__legend-bar {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
}
.asm-map__stats {
    display: flex;
    flex-wrap: wrap;
    gap: 1.25rem;
}
.asm-map__stat {
    display: flex;
    flex-direction: column;
    min-width: 4.5rem;
}
.asm-map__stat-value {
    font-size: 1.35rem;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.1;
}
.asm-map__stat-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #94a3b8;
    margin-top: 0.2rem;
}
.asm-map__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.85rem;
}
.asm-map__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
}
.asm-bed-swatch {
    width: 0.85rem;
    height: 0.85rem;
    border-radius: 0.3rem;
    display: inline-block;
}
.asm-bed-swatch--occupied {
    background: linear-gradient(180deg, #fb923c 0%, #ea580c 100%);
}
.asm-bed-swatch--free {
    background: #fff;
    border: 1.5px dashed #cbd5e1;
}
.asm-map__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
}
@media (min-width: 1024px) {
    .asm-map__grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
.asm-villa {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 1rem;
    overflow: hidden;
    box-shadow: 0 8px 24px -18px rgba(15, 23, 42, 0.35);
}
.asm-villa__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1rem 1.1rem;
    background: linear-gradient(135deg, #fff7ed 0%, #ffffff 70%);
    border-bottom: 1px solid #fed7aa;
}
.asm-villa__title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 800;
    color: #9a3412;
}
.asm-villa__sub {
    margin: 0.2rem 0 0;
    font-size: 0.75rem;
    color: #94a3b8;
    font-weight: 600;
}
.asm-villa__badge {
    flex-shrink: 0;
    padding: 0.35rem 0.65rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 800;
    border: 1px solid transparent;
}
.asm-villa__badge--empty {
    background: #f8fafc;
    border-color: #e2e8f0;
    color: #64748b;
}
.asm-villa__badge--partial {
    background: #fff7ed;
    border-color: #fdba74;
    color: #c2410c;
}
.asm-villa__badge--full {
    background: #ffedd5;
    border-color: #fb923c;
    color: #9a3412;
}
.asm-villa__rooms {
    padding: 0.85rem 1.1rem 1.1rem;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}
.asm-room__label {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 0.45rem;
}
.asm-room__name {
    font-size: 0.8rem;
    font-weight: 700;
    color: #334155;
}
.asm-room__meta {
    font-size: 0.7rem;
    font-weight: 600;
    color: #94a3b8;
}
.asm-room__beds {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
}
.asm-bed {
    min-width: 4.75rem;
    max-width: 7rem;
    flex: 1 1 4.75rem;
    text-align: left;
    border-radius: 0.7rem;
    padding: 0.55rem 0.6rem;
    border: 1px solid transparent;
    cursor: pointer;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.asm-bed:hover {
    transform: translateY(-1px);
}
.asm-bed--occupied {
    background: linear-gradient(180deg, #fff7ed 0%, #ffedd5 100%);
    border-color: #fdba74;
    box-shadow: 0 6px 14px -10px rgba(234, 88, 12, 0.55);
}
.asm-bed--free {
    background: #f8fafc;
    border: 1.5px dashed #cbd5e1;
}
.asm-bed--free:hover {
    border-color: #94a3b8;
    background: #fff;
}
.asm-bed__slot {
    display: block;
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #94a3b8;
}
.asm-bed--occupied .asm-bed__slot {
    color: #c2410c;
}
.asm-bed__student {
    display: block;
    margin-top: 0.2rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: #9a3412;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.asm-bed__student--muted {
    color: #94a3b8;
    font-weight: 600;
}
</style>
